import { useCallback, useState } from "react";
import { FlightAlertsService } from "../../../api/flightAlerts.service";
import { FlightDirection, FlightFixture, FlightLookupError } from "../types";
import {
  EMPTY_RECORD,
  mapFlightCardResponse,
  mapRecordPlateResponse,
  toBackendDirection,
} from "./mapFlightCardResponse";
import { validateFlightQuery } from "./validation.helper";

export interface UseFlightLookupResult {
  data: FlightFixture[] | null;
  isLoading: boolean;
  isError: boolean;
  error: FlightLookupError | null;
  lookup: (
    direction: FlightDirection,
    flightNumberRaw: string,
    isoDate: string,
  ) => void;
  reset: () => void;
}

/**
 * `getToken` mints a fresh reCAPTCHA token right before the real call — v3/Enterprise tokens are
 * short-lived and single-use, so it must not be a value captured once back at the bot-check step.
 */
export const useFlightLookup = (
  getToken: (action: string) => Promise<string>,
): UseFlightLookupResult => {
  const [data, setData] = useState<FlightFixture[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<FlightLookupError | null>(null);

  const lookup = useCallback(
    (direction: FlightDirection, flightNumberRaw: string, isoDate: string) => {
      const validation = validateFlightQuery(flightNumberRaw, isoDate);
      if (!validation.valid) {
        setError({ type: validation.error! });
        setData(null);
        return;
      }

      setIsLoading(true);
      setError(null);
      setData(null);

      const service = new FlightAlertsService();
      const backendDirection = toBackendDirection(direction);

      getToken("flight_lookup")
        .then((recaptchaToken) =>
          service.lookupFlight(backendDirection, flightNumberRaw, isoDate, recaptchaToken),
        )
        .then(async ({ data: cards }) => {

          let record = EMPTY_RECORD;
          try {
            const { data: plate } = await service.getRecordPlate(cards[0]!.designator, backendDirection);
            record = mapRecordPlateResponse(plate);
          } catch {

          }
          setData(cards.map((card) => ({ ...mapFlightCardResponse(card, isoDate), record })));
        })
        .catch((err) => {

          setError({ type: "notFound" });
          console.error("Flight lookup failed", err);
        })
        .finally(() => setIsLoading(false));
    },
    [getToken],
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return { data, isLoading, isError: !!error, error, lookup, reset };
};
